/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Copyright from './Copyright';
import Logo from './Logo';
import Form from './Form';
import { CONTACT_SUPPORT_LINK, SUPPORT_EMAIL } from '../constants';

export function PaperWithForm({ Title, Form, OptionalMessage, sx }) {
  return (
    <Paper
      elevation={4}
      sx={{
        p: 4,
        width: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...sx,
      }}
    >
      <Logo />
      {OptionalMessage}
      <Typography sx={{ pt: 2 }} variant="h5">
        {Title}
      </Typography>
      {Form}
      <Copyright sx={{ pt: 4 }} />
    </Paper>
  );
}

export function SignInSignUpContainer({ Form, NoticeBeforeTitle, title }) {
  const hasAvailableSpaceForWelcomeInfo = useMediaQuery('(min-width:1001px)');

  return (
    <GreyBackgroundWithCenteredChildren>
      <Paper
        elevation={4}
        sx={{
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {hasAvailableSpaceForWelcomeInfo ? (
          <>
            <Stack
              sx={{
                p: 4,
                width: '600px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                textAlign: 'justify',
                justifyContent: 'center',
                gap: 1,
              }}
            >
              <Typography variant="h5">About AllegroGraph Cloud</Typography>
              <Typography>
                AllegroGraph Cloud is a platform that simplifies the process of
                setting up AllegroGraph servers, allowing you to do so with just
                a few clicks.
              </Typography>
              <Typography>
                It's designed for both newcomers interested in learning about
                AllegroGraph and experienced users looking for a convenient way
                to utilize it. Sign up and you'll be able to create a fully
                managed AllegroGraph server for free and use it right away.
              </Typography>
              <Typography variant="h5">New to AllegroGraph?</Typography>
              <Typography>
                It is a high-performance, persistent graph database (
                <Link
                  href="https://allegrograph.com/products/allegrograph/"
                  target="_blank"
                >
                  click to learn more
                </Link>
                ).
              </Typography>
              <Typography variant="h5">Feedback is appreciated!</Typography>
              <Typography>
                We're actively developing AllegroGraph Cloud with plans to
                expand its capabilities and offerings. Your feedback is
                invaluable to us. Please share your experiences and suggestions
                to <Link href={CONTACT_SUPPORT_LINK}>{SUPPORT_EMAIL}</Link> and
                help shape the future of AllegroGraph Cloud!
              </Typography>
            </Stack>
            <Divider orientation="vertical" flexItem />
          </>
        ) : null}
        <Stack
          sx={{
            p: 4,
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Logo />
          {NoticeBeforeTitle}
          <Typography sx={{ pt: 2 }} variant="h5">
            {title}
          </Typography>
          {Form}
          <Copyright sx={{ pt: 4 }} />
        </Stack>
      </Paper>
    </GreyBackgroundWithCenteredChildren>
  );
}

export function GreyBackgroundWithCenteredChildren({ children }) {
  return (
    <Box
      sx={{
        backgroundColor: '#80808030',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {children}
    </Box>
  );
}

export default function PaperOnCenter({
  Title,
  OptionalMessage,
  ...formProps
}) {
  return (
    <GreyBackgroundWithCenteredChildren>
      <PaperWithForm
        OptionalMessage={OptionalMessage}
        Title={Title}
        Form={<Form {...formProps} />}
      />
    </GreyBackgroundWithCenteredChildren>
  );
}
